import { useEffect, useState } from 'react';
import { onboarding } from '../api.js';
import { REMEDIATIONS } from '@vigie/shared';
import type { RemediationAction } from '@vigie/shared';
import { BlocCommande } from '../components/BlocCommande.js';
import {
  LIBELLÉS_ÉTAPE,
  orientationAprèsDétection,
  position,
  prérequisInstallation,
  étapesPour,
} from './parcours-onboarding.js';
import type { Intention, Étape } from './parcours-onboarding.js';
import type { AptitudeCible, CandidatDétection, EnvironnementSonde } from '../api.js';
import InstallationSiteNeuf from './InstallationSiteNeuf.js';

interface Props {
  organizationId: string;
  onTerminé: () => void;
}

/** Consigne par défaut, celle des hébergements Node.js d'Infomaniak. */
const CONSIGNE_REDÉMARRAGE =
  'Manager Infomaniak → Hébergement → le site Node.js → bouton « Redémarrer ».';

const LIBELLÉS_TYPE: Record<string, string> = {
  ghost_cli_systemd: 'ghost-cli + systemd (VPS)',
  ghost_cli_local: 'ghost-cli sans systemd (mutualisé)',
  docker_compose: 'Docker Compose',
  custom: 'commandes personnalisées',
};

/**
 * Assistant d'ajout d'un site.
 *
 * Trois principes guident l'enchaînement :
 * - **l'identité d'abord** : nom et URL sont demandés avant toute connexion, parce que la détection
 *   et l'installation s'en servent, et qu'on ne saisit pas deux fois la même chose ;
 * - la clé privée est **générée par le serveur** et n'apparaît jamais ici. On ne montre que la clé
 *   publique et la commande à exécuter sur le serveur cible — et rien n'est engendré du tout quand
 *   la connexion se fait par mot de passe ;
 * - la détection propose, elle ne décide pas. Le type retenu est toujours confirmé à la main, avec
 *   les indices sous les yeux : se tromper signifierait lancer la mauvaise commande de redémarrage
 *   sur le site d'un média.
 */
export function Onboarding({ organizationId, onTerminé }: Props) {
  const [étape, setÉtape] = useState<Étape>('identité');
  const [intention, setIntention] = useState<Intention | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const [publicKey, setPublicKey] = useState('');
  const [sshHost, setSshHost] = useState('');
  const [sshPort, setSshPort] = useState(22);
  const [méthodeAuth, setMéthodeAuth] = useState<'cle' | 'mdp'>('cle');
  const [sshPassword, setSshPassword] = useState('');
  const [sshUser, setSshUser] = useState('vigie');
  const [empreinte, setEmpreinte] = useState('');
  const [environnement, setEnvironnement] = useState<EnvironnementSonde | null>(null);
  const [méthodeSauvegarde, setMéthodeSauvegarde] = useState<'restic' | 'streamed'>('restic');
  const [résultatRemédiation, setRésultatRemédiation] = useState<string | null>(null);
  const [aperçu, setAperçu] = useState<{
    texte: string;
    remplacements: Record<string, number>;
    configured: boolean;
  } | null>(null);
  const [analyse, setAnalyse] = useState<{ analyse: string; modèle: string } | null>(null);
  /**
   * Ce que la machine permet, constaté au test de connexion.
   *
   * Le formulaire d'installation s'en sert pour demander un mot de passe sudo quand il en faut un,
   * et pour refuser tout de suite ce que Vigie ne sait pas faire — plutôt que de le découvrir au
   * bout de vingt minutes d'installation.
   */
  const [aptitude, setAptitude] = useState<AptitudeCible | null>(null);
  /** Installation lancée : ce qui permet de retrouver le suivi après un rechargement de page. */
  const [installationId, setInstallationId] = useState<string | null>(null);

  const [candidats, setCandidats] = useState<CandidatDétection[]>([]);
  const [typeRetenu, setTypeRetenu] = useState('');
  const [paramsJson, setParamsJson] = useState('');
  const [script, setScript] = useState<{ script: string; warnings: string[] } | null>(null);

  const [nom, setNom] = useState('');
  const [url, setUrl] = useState('');
  /**
   * Le redémarrage appartient-il à l'hébergeur ?
   *
   * Coché d'office quand la sonde a trouvé un site qui répond sans qu'aucun process Ghost ne soit
   * observable : c'est la signature d'une application supervisée par le fournisseur, où
   * `ghost restart` lancerait un second process se disputant le port.
   */
  const [redémarrageExterne, setRedémarrageExterne] = useState(false);

  /**
   * Détaille une erreur de validation.
   *
   * Le serveur renvoie le champ fautif et la raison ; n'afficher que « Données invalides » revenait
   * à jeter cette information et à laisser chercher dans un JSON de paramètres. C'est précisément
   * le moment où l'on a le moins de contexte pour deviner.
   */
  function détailler(data: { error?: string; details?: unknown }, status: number): string {
    const base = data.error ?? `Échec (code ${String(status)})`;
    if (!Array.isArray(data.details)) return base;

    const champs = (data.details as Array<{ path?: unknown; message?: unknown }>)
      .map((problème) => {
        const chemin = Array.isArray(problème.path) ? problème.path.join('.') : '';
        const message = typeof problème.message === 'string' ? problème.message : 'valeur refusée';
        return chemin === '' ? message : `${chemin} : ${message}`;
      })
      .filter((ligne) => ligne !== '');

    return champs.length === 0 ? base : `${base} — ${champs.join(' ; ')}`;
  }

  /**
   * Reprise de l'assistant, à l'ouverture.
   *
   * Il vivait entièrement dans l'état du navigateur, alors que la session serveur porte déjà
   * l'identité du site, la connexion et l'empreinte de la clé d'hôte. Un rechargement de page au
   * milieu du parcours perdait donc un travail que rien n'avait perdu — et pendant une installation,
   * qui dure une demi-heure, c'était le geste le plus naturel du monde qui coûtait le plus cher.
   */
  useEffect(() => {
    let annulé = false;
    void (async () => {
      const réponse = await onboarding.état(organizationId);
      if (annulé || réponse.status !== 200 || !réponse.data.enCours) return;
      const état = réponse.data;
      if (état.name !== null && état.name !== undefined) setNom(état.name);
      if (état.url !== null && état.url !== undefined) setUrl(état.url);
      if (état.intention !== null && état.intention !== undefined) setIntention(état.intention);
      if (état.sshHost !== null && état.sshHost !== undefined) setSshHost(état.sshHost);
      if (état.sshPort !== undefined) setSshPort(état.sshPort);
      if (état.sshUser !== null && état.sshUser !== undefined) setSshUser(état.sshUser);
      if (état.hostKeyFingerprint !== null && état.hostKeyFingerprint !== undefined) {
        setEmpreinte(état.hostKeyFingerprint);
      }
      if (état.moyen === 'mot_de_passe') setMéthodeAuth('mdp');
      // Une installation en cours l'emporte sur tout : c'est là qu'il faut revenir.
      if (état.installationId !== null && état.installationId !== undefined) {
        setInstallationId(état.installationId);
        setÉtape('installation');
      }
    })();
    return () => {
      annulé = true;
    };
  }, [organizationId]);

  async function exécuter<T>(
    action: () => Promise<{ status: number; data: T & { error?: string; details?: unknown } }>,
  ) {
    setErreur(null);
    setEnCours(true);
    try {
      const { status, data } = await action();
      if (status >= 400) {
        setErreur(détailler(data, status));
        return null;
      }
      return data;
    } catch {
      setErreur('Service injoignable');
      return null;
    } finally {
      setEnCours(false);
    }
  }

  /** Ouvre la session d'assistant : c'est cette étape, et non la clé, qui la crée désormais. */
  async function démarrer() {
    const données = await exécuter(() => onboarding.démarrer(organizationId, { name: nom, url }));
    if (données === null) return;
    setÉtape('connexion');
  }

  /**
   * Engendre la paire de clés du site.
   *
   * Appelée seulement dans la branche « clé » : sur un hébergement qui n'accepte pas de clé, en
   * engendrer une laisserait une clé privée en base pour un site qui ne s'en servira jamais.
   */
  async function générerClés() {
    const données = await exécuter(() => onboarding.clés(organizationId));
    if (données === null) return;
    setPublicKey(données.publicKey);
  }

  async function testerSsh() {
    const données = await exécuter(() =>
      onboarding.testerSsh(organizationId, {
        sshHost,
        sshPort,
        sshUser,
        ...(méthodeAuth === 'mdp' ? { sshPassword } : {}),
      }),
    );
    if (données === null) return;
    setEmpreinte(données.hostKeyFingerprint);
    setEnvironnement(données.environment);
    setMéthodeSauvegarde(données.backupMethod);
    setAptitude(données.aptitude ?? null);
    setÉtape('serveur');
  }

  /**
   * Détection de l'installation en place, puis orientation.
   *
   * Elle tourne maintenant sur les deux parcours, parce que c'est elle qui permet de proposer une
   * réponse plutôt que de poser la question à l'aveugle. Ne rien trouver n'est plus un échec
   * apparent : c'est le constat qui oriente vers l'installation, et l'écran le dit dans ces termes.
   *
   * Ce qu'elle ne fait pas : décider. Un hôte qui héberge déjà un média peut en accueillir un
   * second, donc la suggestion est pré-sélectionnée et non imposée.
   */
  async function détecterEtOrienter() {
    const détection = await exécuter(() => onboarding.détecter(organizationId, {}));
    if (détection === null) return;
    setCandidats(détection.candidates);
    if (détection.best !== null) {
      setTypeRetenu(détection.best.installType);
      setParamsJson(
        JSON.stringify(
          { installType: détection.best.installType, ...détection.best.suggestedParams },
          null,
          2,
        ),
      );
    }
    setIntention(orientationAprèsDétection(détection.candidates).suggestion);
    setÉtape('intention');
  }

  /**
   * L'intention, confirmée au serveur.
   *
   * C'est ici que l'adresse d'un site neuf est refusée si elle ne peut pas porter de certificat :
   * le contrôle a suivi le choix, il n'a pas disparu.
   */
  async function confirmerIntention() {
    if (intention === null) return;
    const données = await exécuter(() => onboarding.intention(organizationId, { intention }));
    if (données === null) return;
    setÉtape(intention === 'neuf' ? 'installation' : 'détection');
  }

  /**
   * Remédiation menée par Vigie, sur la connexion déjà ouverte.
   *
   * Le navigateur envoie un **code** d'action, jamais une commande : c'est le serveur qui décide de
   * ce qui s'exécute à distance, et la liste des actions possibles y est close.
   */
  async function remédier(action: RemediationAction) {
    const données = await exécuter(() => onboarding.remédier(organizationId, action));
    if (données === null) return;
    setEnvironnement(données.environment);
    setMéthodeSauvegarde(données.backupMethod);
    setRésultatRemédiation(
      données.source === 'pushed'
        ? `restic déposé sur le serveur (${données.binary}), empreinte vérifiée. Les sauvegardes utiliseront ce mode.`
        : `restic était déjà disponible sur l’hôte (${données.binary}).`,
    );
  }

  /** Faits transmis à l'assistant : choisis un par un, jamais l'objet entier. */
  function faitsPourAnalyse(): Record<string, string> {
    return {
      système: environnement === null ? 'inconnu' : `${environnement.os} ${environnement.arch}`,
      'binaires présents': environnement?.availableBinaries.join(', ') ?? 'inconnus',
      'binaires absents': environnement?.missingBinaries.join(', ') ?? 'inconnus',
      contraintes: environnement?.restrictions.map((c) => c.message).join(' ; ') ?? 'aucune',
      'pistes de détection': candidats
        .map((c) => `${c.installType} (${String(c.confidence)} %) : ${c.evidence.join(', ')}`)
        .join(' | '),
    };
  }

  async function demanderAperçu() {
    const données = await exécuter(() =>
      onboarding.aperçuAnalyse(organizationId, { faits: faitsPourAnalyse() }),
    );
    if (données !== null) {
      setAperçu(données);
      setAnalyse(null);
    }
  }

  async function lancerAnalyse() {
    const données = await exécuter(() =>
      onboarding.analyser(organizationId, { faits: faitsPourAnalyse() }),
    );
    if (données !== null) setAnalyse({ analyse: données.analyse, modèle: données.modèle });
  }

  async function obtenirScript() {
    const données = await exécuter(() =>
      onboarding.script(organizationId, { installType: typeRetenu, username: sshUser }),
    );
    if (données !== null) setScript({ script: données.script, warnings: données.warnings });
  }

  async function enregistrer() {
    let installParams: unknown;
    try {
      installParams = JSON.parse(paramsJson);
    } catch {
      setErreur('Les paramètres d’installation ne sont pas un JSON valide.');
      return;
    }

    // Un champ vide vient presque toujours d'une détection qui n'a pas pu conclure — le dossier
    // Ghost quand il n'y a pas de .ghost-cli, par exemple. Le dire ici évite un aller-retour avec
    // le serveur pour apprendre la même chose.
    const vides = Object.entries(installParams as Record<string, unknown>)
      .filter(([, valeur]) => valeur === '' || valeur === null)
      .map(([clé]) => clé);
    if (vides.length > 0) {
      setErreur(
        `À compléter dans les paramètres d’installation : ${vides.join(', ')}. ` +
          'La détection n’a pas pu déterminer ces valeurs ; renseigne-les à la main.',
      );
      return;
    }
    const données = await exécuter(() =>
      onboarding.terminer(organizationId, {
        name: nom,
        url,
        installParams,
        restartMode: redémarrageExterne ? 'external' : 'driver',
        restartHint: redémarrageExterne ? CONSIGNE_REDÉMARRAGE : null,
      }),
    );
    if (données === null) return;
    setÉtape('fini');
  }

  const ordre = étapesPour(intention);
  const positionCourante = position(intention, étape).rang;

  /**
   * Renonce, et n'en laisse rien.
   *
   * Une session d'assistant en cours détient une clé privée chiffrée, parfois un mot de passe de
   * compte. Quitter l'écran sans l'effacer laissait tout cela en session jusqu'à son expiration,
   * sans usage donc sans raison.
   */
  async function abandonner() {
    await onboarding.abandonner(organizationId);
    onTerminé();
  }

  return (
    <section className="carte">
      <h2>Ajouter un site</h2>
      <p className="aide">
        Vigie se connecte en SSH au serveur du site : rien n’est installé dessus, et aucun agent n’y
        tourne. Les étapes ci-dessous nomment le site, établissent cet accès, vérifient l’identité
        du serveur et reconnaissent le type d’installation de Ghost.
      </p>

      <ol className="fil-etapes">
        {ordre.map((clé, index) => (
          <li
            key={clé}
            className={
              index < positionCourante
                ? 'fil-etape fil-etape--faite'
                : index === positionCourante
                  ? 'fil-etape fil-etape--courante'
                  : 'fil-etape'
            }
          >
            {index + 1}. {LIBELLÉS_ÉTAPE[clé]}
          </li>
        ))}
      </ol>

      {erreur !== null && <p className="erreur">{erreur}</p>}

      {étape !== 'fini' && (
        <button type="button" className="lien" onClick={() => void abandonner()}>
          Renoncer et revenir à la flotte
        </button>
      )}

      {étape === 'identité' && (
        <>
          <h3>Quel site ajoutes-tu ?</h3>
          <p className="aide">
            On commence par là, avant même la connexion : l’URL sert à reconnaître l’installation de
            Ghost sur le serveur, et le nom donne son emplacement lisible dans le dépôt de
            sauvegarde. Les demander en dernier obligeait à les saisir deux fois.
          </p>
          <label htmlFor="nom">Nom du site</label>
          <input id="nom" value={nom} onChange={(e) => setNom(e.target.value)} required />
          <p className="aide">
            Il sert partout dans l’interface, les alertes et l’audit — et c’est lui qu’il faudra
            saisir pour confirmer une restauration. Choisis-le reconnaissable.
          </p>
          <label htmlFor="url">URL publique</label>
          <input
            id="url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://media.exemple.fr"
            required
          />
          <p className="aide">
            Celle que voient les visiteuses et visiteurs, schéma compris. Vigie l’interroge toutes
            les cinq minutes pour mesurer disponibilité et latence, et s’en sert pour vérifier qu’un
            site est bien revenu après un redémarrage ou une mise à jour. Une URL erronée produirait
            des alertes de panne sur un site parfaitement sain.
          </p>
          <button
            type="button"
            onClick={() => void démarrer()}
            disabled={enCours || nom === '' || url === ''}
          >
            {enCours ? 'Enregistrement…' : 'Continuer'}
          </button>
        </>
      )}

      {étape === 'connexion' && (
        <>
          <h3>Comment Vigie se connecte-t-elle ?</h3>
          <div className="bascule-mode">
            <button
              type="button"
              className={méthodeAuth === 'cle' ? 'onglet onglet--actif' : 'onglet'}
              onClick={() => setMéthodeAuth('cle')}
            >
              Clé SSH (recommandé)
            </button>
            <button
              type="button"
              className={méthodeAuth === 'mdp' ? 'onglet onglet--actif' : 'onglet'}
              onClick={() => setMéthodeAuth('mdp')}
            >
              Mot de passe
            </button>
          </div>
          <p className="aide">
            La clé reste préférable : elle se restreint dans <code>authorized_keys</code> —
            commandes autorisées, adresses d’origine — là où un mot de passe ouvre le compte entier.
            Mais certains hébergements mutualisés, dont des Infomaniak, ne donnent pas accès à ce
            fichier : aucune clé n’y est installable, et le mot de passe est alors le seul moyen.
          </p>

          {méthodeAuth === 'mdp' ? (
            <>
              <p className="avertissement">
                Avec un mot de passe, le compte SSH restreint n’est pas possible : le secret
                enregistré vaut accès complet au compte. Vigie le conserve chiffré par la clé
                maîtresse, comme tout secret, mais l’affaiblissement est réel et vaut d’être su.
              </p>
              <label htmlFor="mdp-ssh">Mot de passe SSH</label>
              <input
                id="mdp-ssh"
                type="password"
                autoComplete="new-password"
                value={sshPassword}
                onChange={(e) => setSshPassword(e.target.value)}
              />
              <p className="aide">
                Celui du compte d’hébergement, tel que tu l’utilises pour t’y connecter. Il ne
                repartira jamais vers ce navigateur une fois enregistré.
              </p>
            </>
          ) : (
            <AutorisationParClé
              publicKey={publicKey}
              enCours={enCours}
              onEngendrer={() => void générerClés()}
            />
          )}

          <label htmlFor="hôte">Hôte SSH</label>
          <input id="hôte" value={sshHost} onChange={(e) => setSshHost(e.target.value)} required />
          <label htmlFor="port">Port</label>
          <input
            id="port"
            type="number"
            value={sshPort}
            onChange={(e) => setSshPort(Number(e.target.value))}
          />
          <label htmlFor="utilisateur">Utilisateur</label>
          <input
            id="utilisateur"
            value={sshUser}
            onChange={(e) => setSshUser(e.target.value)}
            required
          />
          <button
            type="button"
            onClick={() => void testerSsh()}
            disabled={
              enCours ||
              sshHost === '' ||
              (méthodeAuth === 'cle' ? publicKey === '' : sshPassword === '')
            }
          >
            {enCours ? 'Connexion…' : 'Tester la connexion et détecter'}
          </button>
          {méthodeAuth === 'cle' && publicKey === '' && (
            <p className="aide">
              Engendre d’abord la paire de clés, puis dépose-la sur le serveur.
            </p>
          )}
        </>
      )}

      {étape === 'intention' && (
        <>
          <h3>Que veux-tu faire ?</h3>
          {orientationAprèsDétection(candidats).ghostTrouvé ? (
            <p className="note">
              Vigie a reconnu une installation Ghost sur ce serveur —{' '}
              <strong>
                {LIBELLÉS_TYPE[candidats[0]?.installType ?? ''] ?? candidats[0]?.installType}
              </strong>
              , confiance {candidats[0]?.confidence} %. L’adoption est donc pré-sélectionnée.
            </p>
          ) : (
            <p className="note">
              Vigie n’a trouvé aucune installation Ghost sur ce serveur. Ce n’est pas un échec :
              c’est l’état attendu d’une machine sur laquelle on vient en poser une.
            </p>
          )}
          <p className="aide">
            Le choix reste le tien : un serveur qui héberge déjà un média peut parfaitement en
            accueillir un second, et ce que Vigie a trouvé ne dit pas lequel des deux gestes tu es
            venu faire. Les deux chemins se terminent par le même enregistrement — un site installé
            par Vigie est inscrit et vérifié exactement comme un site adopté.
          </p>

          <div className="choix-parcours">
            <button
              type="button"
              className={
                intention === 'existant' ? 'carte-choix carte-choix--actif' : 'carte-choix'
              }
              onClick={() => setIntention('existant')}
            >
              <strong>Ghost est déjà installé</strong>
              <span>
                Vigie se connecte, reconnaît le type d’installation — ghost-cli, Docker, montage sur
                mesure — et t’en montre les indices avant que tu ne confirmes.
              </span>
            </button>
            <button
              type="button"
              className={intention === 'neuf' ? 'carte-choix carte-choix--actif' : 'carte-choix'}
              onClick={() => setIntention('neuf')}
              disabled={!prérequisInstallation(aptitude).ok}
            >
              <strong>Installer Ghost sur ce serveur</strong>
              <span>
                Vigie pose une pile Docker — Ghost et sa base — avec ses mots de passe engendrés et
                conservés chiffrés. Docker est le seul prérequis ; le TLS reste au proxy de façade.
              </span>
            </button>
          </div>

          {/*
            L'impossibilité se dit ici, sur le choix qu'elle ferme, et pas ailleurs.
            À l'écran précédent elle s'affichait en rouge sous un relevé qui vaut pour les deux
            parcours, et se lisait comme un défaut du serveur — alors qu'elle ne concerne que
            l'installation : un système hors liste accueille très bien un Ghost qu'on vient adopter.
          */}
          {prérequisInstallation(aptitude).raisons.map((raison) => (
            <p key={raison} className="avertissement">
              Installation impossible ici : {raison}
            </p>
          ))}

          <div className="actions">
            <button
              type="button"
              onClick={() => void confirmerIntention()}
              disabled={intention === null || enCours}
            >
              {enCours ? 'En cours…' : 'Continuer'}
            </button>
            {/*
              Retour vers l'identité : une adresse qui ne peut pas porter de certificat est refusée
              ici, et la corriger se fait deux écrans plus haut. Sans ce lien, le message d'erreur
              n'aurait nulle part où mener.
            */}
            <button type="button" className="lien" onClick={() => setÉtape('identité')}>
              Corriger le nom ou l’adresse
            </button>
          </div>
        </>
      )}

      {étape === 'serveur' && (
        <>
          <h3>Vérifier l’identité du serveur</h3>
          <p className="aide">
            Voici l’empreinte de la clé d’hôte présentée par le serveur pendant le test. Compare-la
            avec celle obtenue <strong>par un autre chemin</strong> — depuis le serveur lui-même, ou
            depuis ton poste — car c’est cette comparaison, et elle seule, qui écarte une
            interception.
          </p>
          <code className="secret">{empreinte}</code>
          <BlocCommande
            où="à exécuter sur ton poste, ou sur le serveur"
            contenu={`ssh-keyscan -t ed25519,rsa ${sshHost} 2>/dev/null | ssh-keygen -lf -`}
          />
          <p className="aide">
            Une fois acceptée, cette empreinte est mémorisée et vérifiée à chaque connexion : toute
            divergence bloque l’accès et déclenche une alerte de sécurité, plutôt que de poursuivre
            en silence. Si l’empreinte affichée ne correspond pas, n’enregistre pas ce site et
            cherche pourquoi.
          </p>

          {aptitude !== null && (
            <div className="carte-interne">
              <h4>Ce que ce serveur permet</h4>
              <p className="aide">
                Constaté sans rien modifier. Ce relevé sert au choix de l’étape suivante : il dit ce
                que Vigie saurait faire ici, plutôt que de le laisser découvrir au bout de vingt
                minutes d’installation. Un système absent de la liste n’empêche pas d’adopter un
                Ghost qui y tourne déjà — il empêche d’en installer un.
              </p>
              <dl className="recapitulatif">
                <dt>Système</dt>
                <dd>
                  {aptitude.systeme.nom} ({aptitude.architecture})
                  {aptitude.systeme.prisEnCharge ? '' : ' — non pris en charge'}
                </dd>
                <dt>Privilèges</dt>
                <dd>
                  {aptitude.privileges === 'root'
                    ? 'compte root'
                    : aptitude.privileges === 'sudo'
                      ? 'sudo sans mot de passe'
                      : aptitude.privileges === 'sudo_mdp_requis'
                        ? 'sudo, avec mot de passe — il sera demandé'
                        : 'aucune élévation possible'}
                </dd>
              </dl>
              {aptitude.reserves.map((réserve) => (
                <p key={réserve} className="avertissement">
                  {réserve}
                </p>
              ))}
            </div>
          )}

          {environnement !== null && environnement.restrictions.length > 0 && (
            <>
              <h3>Contraintes détectées</h3>
              <p className="aide">
                Ce que Vigie n’a pas trouvé sur cet hôte. Rien de bloquant en soi : chaque manque se
                traduit par une fonctionnalité dégradée, expliquée dans la matrice de compatibilité,
                et non par un échec silencieux.
              </p>
              {environnement.restrictions.map((restriction) => {
                const remédiation = REMEDIATIONS[restriction.code];
                return (
                  <div key={restriction.code} className="carte-interne">
                    <h4>{remédiation.titre}</h4>
                    <p className="avertissement">{remédiation.consequence}</p>
                    <ul className="liste-options">
                      {remédiation.options.map((option) => (
                        <li key={option.titre}>
                          <div className="version-entete">
                            <strong>{option.titre}</strong>
                            {option.parDefaut === true && (
                              <span className="etiquette">retenu si tu ne fais rien</span>
                            )}
                          </div>
                          <p className="aide">{option.effet}</p>
                          {option.commande !== undefined && (
                            <BlocCommande
                              où="à exécuter sur le serveur du site"
                              contenu={option.commande}
                            />
                          )}
                          {option.action !== undefined && (
                            <div className="actions actions--serrees">
                              <button
                                type="button"
                                disabled={enCours}
                                onClick={() => void remédier(option.action as RemediationAction)}
                              >
                                {enCours ? 'En cours…' : 'Laisser Vigie s’en charger'}
                              </button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              {résultatRemédiation !== null && <p className="succes">{résultatRemédiation}</p>}
            </>
          )}
          <h3>Méthode de sauvegarde</h3>
          <p className="aide">
            Retenue automatiquement : <strong>{méthodeSauvegarde}</strong>.{' '}
            {méthodeSauvegarde === 'restic'
              ? 'restic est disponible sur l’hôte : les sauvegardes sont déduplication comprise, restaurables par composant, et vérifiables.'
              : 'restic est absent et ne peut pas être installé ici : l’archive est produite sur le serveur, chiffrée au passage par le plan de contrôle, et déposée en flux sur le dépôt S3. Aucun binaire n’est installé sur le serveur.'}
          </p>

          <div className="actions">
            <button type="button" disabled={enCours} onClick={() => void détecterEtOrienter()}>
              {enCours ? 'Reconnaissance…' : 'Regarder ce qu’il y a sur ce serveur'}
            </button>
          </div>
        </>
      )}

      {étape === 'installation' && (
        <InstallationSiteNeuf
          organizationId={organizationId}
          nom={nom}
          url={url}
          aptitude={aptitude}
          installationId={installationId}
          onInstallationLancée={setInstallationId}
          onTerminée={() => setÉtape('fini')}
          onRetour={() => setÉtape('intention')}
        />
      )}

      {étape === 'détection' && (
        <>
          <h3>Type d’installation</h3>
          <p className="aide">
            Déduit des indices relevés sur le serveur, avec un degré de confiance. Vérifie le choix
            : c’est lui qui détermine comment Vigie redémarrera le site, lira sa configuration et
            appliquera ses mises à jour. Tu peux retenir un autre type que celui proposé.
          </p>
          {candidats.map((candidat) => (
            <label key={candidat.installType} className="candidat">
              <input
                type="radio"
                name="type"
                checked={typeRetenu === candidat.installType}
                onChange={() => {
                  setTypeRetenu(candidat.installType);
                  setParamsJson(
                    JSON.stringify(
                      { installType: candidat.installType, ...candidat.suggestedParams },
                      null,
                      2,
                    ),
                  );
                }}
              />
              <span>
                {LIBELLÉS_TYPE[candidat.installType] ?? candidat.installType} — confiance{' '}
                {candidat.confidence} %<small>{candidat.evidence.join(' · ')}</small>
              </span>
            </label>
          ))}

          <h3>Analyse assistée</h3>
          <p className="aide">
            Si la détection ne tranche pas — installation sur mesure, arborescence inhabituelle — un
            assistant peut examiner le diagnostic et proposer des pistes. Il{' '}
            <strong>n’a aucun accès au serveur</strong> et ne peut rien y exécuter : sa réponse est
            un texte, et les commandes qu’il suggère sont à relire avant d’être lancées.
          </p>
          <p className="aide">
            Les données quittent alors le plan de contrôle vers Mistral (France). Rien n’est envoyé
            sans que tu aies vu exactement quoi.
          </p>

          {aperçu === null ? (
            <button
              type="button"
              className="lien"
              disabled={enCours}
              onClick={() => void demanderAperçu()}
            >
              {enCours ? 'Préparation…' : 'Voir ce qui serait transmis'}
            </button>
          ) : (
            <div className="carte-interne">
              <h4>Ce qui serait transmis, mot pour mot</h4>
              {Object.keys(aperçu.remplacements).length > 0 && (
                <p className="aide">
                  Caviardé avant envoi :{' '}
                  {Object.entries(aperçu.remplacements)
                    .map(([catégorie, nombre]) => `${String(nombre)} ${catégorie}`)
                    .join(', ')}
                  .
                </p>
              )}
              <pre className="journal">{aperçu.texte}</pre>

              {!aperçu.configured ? (
                <p className="avertissement">
                  Aucun assistant n’est configuré sur cette instance. Rien ne sera envoyé nulle part
                  ; tout le reste de Vigie fonctionne sans.
                </p>
              ) : (
                <div className="actions actions--serrees">
                  <button type="button" disabled={enCours} onClick={() => void lancerAnalyse()}>
                    {enCours ? 'Analyse…' : 'Envoyer ce texte et analyser'}
                  </button>
                  <button type="button" className="lien" onClick={() => setAperçu(null)}>
                    Renoncer
                  </button>
                </div>
              )}

              {analyse !== null && (
                <>
                  <h4>Analyse — à vérifier</h4>
                  <p className="avertissement">
                    Produite par un modèle de langue ({analyse.modèle}) à partir du texte ci-dessus.
                    Elle peut se tromper : traite-la comme une piste, jamais comme un constat.
                  </p>
                  <p className="aide">
                    Les domaines, comptes et adresses ont été rétablis <strong>localement</strong>{' '}
                    dans le texte ci-dessous : le modèle n’a vu que des marqueurs, les commandes te
                    parviennent complètes. Les secrets, eux, ne sont jamais rétablis.
                  </p>
                  <pre className="journal">{analyse.analyse}</pre>
                </>
              )}
            </div>
          )}

          <h3>Paramètres d’installation</h3>
          <p className="aide">
            Pré-remplis à partir de la détection. Selon le type retenu : <code>ghostDir</code> est
            le dossier de l’installation ghost-cli, <code>composePath</code> le chemin du fichier
            compose et <code>serviceName</code> le service Ghost qu’il déclare, et{' '}
            <code>commands</code> les commandes fournies pour une installation sur mesure. Un chemin
            erroné se verra au premier redémarrage : mieux vaut le corriger ici.
          </p>
          <textarea
            id="params"
            rows={8}
            value={paramsJson}
            onChange={(e) => setParamsJson(e.target.value)}
          />

          <h3>Compte SSH restreint (recommandé)</h3>
          <p className="aide">
            Tu peux laisser Vigie utiliser le compte actuel. Mais un compte dédié, limité aux seules
            commandes nécessaires à ce type d’installation, réduit ce qu’une compromission du plan
            de contrôle permettrait de faire sur ce serveur. La commande ci-dessous crée ce compte
            et y installe la clé publique déjà engendrée.
          </p>
          <button type="button" className="lien" onClick={() => void obtenirScript()}>
            Afficher la commande de création d’un utilisateur SSH restreint
          </button>
          {script !== null && (
            <>
              <BlocCommande où="à exécuter sur le serveur, en root" contenu={script.script} />
              <p className="aide">
                Une fois exécutée, reviens à l’étape précédente, remplace le nom d’utilisateur par
                celui que la commande a créé, et relance le test de connexion.
              </p>
              {script.warnings.map((avertissement) => (
                <p key={avertissement} className="erreur">
                  {avertissement}
                </p>
              ))}
            </>
          )}

          <button
            type="button"
            onClick={() => setÉtape('enregistrement')}
            disabled={typeRetenu === ''}
          >
            Continuer
          </button>
        </>
      )}

      {étape === 'enregistrement' && (
        <>
          <h3>Ce qui va être enregistré</h3>
          <dl className="recapitulatif">
            <dt>Nom</dt>
            <dd>{nom}</dd>
            <dt>URL publique</dt>
            <dd>{url}</dd>
            <dt>Accès SSH</dt>
            <dd>
              {sshUser}@{sshHost}:{sshPort} —{' '}
              {méthodeAuth === 'cle' ? 'clé dédiée' : 'mot de passe'}
            </dd>
            <dt>Type d’installation</dt>
            <dd>{LIBELLÉS_TYPE[typeRetenu] ?? typeRetenu}</dd>
            <dt>Sauvegardes</dt>
            <dd>{méthodeSauvegarde}</dd>
          </dl>
          <button type="button" className="lien" onClick={() => setÉtape('identité')}>
            Corriger le nom ou l’URL
          </button>

          {intention === 'neuf' ? (
            <p className="aide">
              Le redémarrage appartient à Vigie sur une pile qu’elle a posée : c’est
              <code> docker compose </code>, toujours. Rien à déclarer ici.
            </p>
          ) : (
            <>
              <h3>Qui redémarre ce site</h3>
              <p className="aide">
                Sur certains hébergements — les mutualisés Infomaniak, notamment — l’application
                Node.js est supervisée par le fournisseur : elle ne tourne pas sous le compte SSH,
                aucune commande ne peut la relancer, et le seul redémarrage réel est un bouton dans
                son panneau. Coche alors la case : après une modification de configuration, Vigie
                écrira le fichier et te dira où cliquer, au lieu de tenter une commande qui
                lancerait un second Ghost se disputant le port.
              </p>
              <div className="case">
                <label>
                  <input
                    type="checkbox"
                    checked={redémarrageExterne}
                    onChange={(e) => setRedémarrageExterne(e.target.checked)}
                  />
                  <span>Le redémarrage se fait hors de Vigie</span>
                </label>
              </div>
              {environnement !== null &&
                environnement.restrictions.some((c) => c.code === 'systemd_absent') &&
                !redémarrageExterne && (
                  <p className="aide">
                    Cet hôte n’a pas de systemd. Si Ghost y est démarré depuis le panneau de
                    l’hébergeur, coche la case ci-dessus — cela se vérifie en cherchant un process
                    Ghost sur le serveur alors que le site répond.
                  </p>
                )}
            </>
          )}
          <p className="aide">
            L’enregistrement applique des réglages par défaut, tous modifiables ensuite depuis la
            fiche du site : sonde toutes les 5 minutes, sauvegarde quotidienne à 3 h 30, rétention
            de 30 jours, et mises à jour <strong>manuelles</strong> — rien ne sera installé sans ton
            accord.
          </p>
          <button
            type="button"
            onClick={() => void enregistrer()}
            disabled={enCours || nom === '' || url === ''}
          >
            {enCours ? 'Vérification…' : 'Enregistrer le site'}
          </button>
        </>
      )}

      {étape === 'fini' && (
        <>
          <h3>Site enregistré</h3>
          <p className="succes">
            La supervision a démarré : la première sonde passe dans les minutes qui viennent, et le
            site apparaît dès maintenant dans la flotte.
          </p>
          <p className="aide">Trois choses valent d’être faites tout de suite :</p>
          <ol className="liste-suite">
            <li>
              <strong>Vérifier qu’un dépôt de sauvegarde est configuré</strong> — écran « Réglages
              de l’organisation ». Sans dépôt, aucune sauvegarde n’est possible, et cela ne se
              verrait qu’au premier passage nocturne.
            </li>
            <li>
              <strong>Lancer une sauvegarde à la main</strong> depuis la fiche du site, sans
              attendre la nuit : c’est la seule façon de savoir que la chaîne fonctionne pour ce
              site-là.
            </li>
            <li>
              <strong>Choisir la politique de mise à jour.</strong> Elle est manuelle par défaut.
              Une version majeure reste soumise à approbation même en automatique.
            </li>
          </ol>
          <button type="button" onClick={onTerminé}>
            Retour à la flotte
          </button>
        </>
      )}
    </section>
  );
}

/**
 * Génération de la paire de clés, puis instructions d'installation de la clé publique.
 *
 * Extrait en composant parce qu'il n'a de sens que dans le mode par clé : l'afficher à côté d'un
 * champ de mot de passe laisserait croire qu'il faut faire les deux. Et la génération vit ici, pas
 * dans une étape à part, pour qu'aucune clé ne soit engendrée quand on choisit le mot de passe.
 */
function AutorisationParClé({
  publicKey,
  enCours,
  onEngendrer,
}: {
  publicKey: string;
  enCours: boolean;
  onEngendrer: () => void;
}) {
  if (publicKey === '') {
    return (
      <>
        <p className="aide">
          Vigie engendre une paire de clés SSH <strong>propre à ce site</strong>. La clé privée
          reste sur le plan de contrôle, chiffrée par la clé maîtresse, et n’en sort jamais — ni
          vers ton navigateur, ni dans un journal. Seule la clé publique s’affiche, à déposer sur le
          serveur.
        </p>
        <p className="aide">
          Une clé par site plutôt qu’une clé commune : retirer l’accès à un site ne touche alors pas
          aux autres, et l’audit dit toujours quelle clé a agi.
        </p>
        <button type="button" onClick={onEngendrer} disabled={enCours}>
          {enCours ? 'Génération…' : 'Engendrer la paire de clés'}
        </button>
      </>
    );
  }

  return (
    <>
      <p className="aide">
        Connecte-toi au serveur du site comme tu le fais d’habitude, puis colle ce bloc. Il ajoute
        la clé aux accès autorisés sans toucher à ceux qui existent déjà.
      </p>
      <BlocCommande
        où="à exécuter sur le serveur du site"
        contenu={`mkdir -p ~/.ssh && chmod 700 ~/.ssh
printf '%s\\n' '${publicKey}' >> ~/.ssh/authorized_keys
chmod 600 ~/.ssh/authorized_keys`}
      />
    </>
  );
}
